import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase F-finale-6 Slice 6b — single source of truth for the IA-side
 * admission-funnel write-gate.
 *
 * Mirrors `useCanEditForms` (Slice 5a). Combines two independent signals:
 *
 *   1. **RBAC** — the principal must hold `manage_admissions` (base role
 *      catalog or designation override). `admin` carries it by default;
 *      staff roles without an override do not.
 *   2. **Tenant capability** — the SA must not have disabled
 *      `admissions_editable_by_tenant`. Default `True` — legacy and
 *      freshly minted tenants are editable out of the box.
 *
 * Returns three orthogonal booleans so consumers render the right
 * surface for each rejection reason:
 *
 *   - `canEditAdmissions` — overall write-eligibility. Hide / disable the
 *     Admission-funnel nav entry + all editor write controls when `false`.
 *   - `missingRbac` — logged in but lacks `manage_admissions`.
 *   - `pausedByTenant` — has the RBAC but the SA paused funnel editing for
 *     the tenant. Surface the paused banner.
 *
 * `pausedByTenant` only fires when `missingRbac === false`, so the two
 * banners never stack.
 */
export interface AdmissionsAuthoringGate {
  canEditAdmissions: boolean;
  missingRbac: boolean;
  pausedByTenant: boolean;
}

export function useCanEditAdmissions(): AdmissionsAuthoringGate {
  const { user, featureFlags } = useAuth();
  return React.useMemo<AdmissionsAuthoringGate>(() => {
    if (!user) {
      return { canEditAdmissions: false, missingRbac: false, pausedByTenant: false };
    }
    const hasRbac = userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_admissions",
      user.permission_mode ?? "additive",
    );
    // `null` (super_admin synthetic tenant / pre-/auth/me) and `undefined`
    // mean "no gating" — matches the BE contract.
    const flagEnabled = featureFlags?.admissions_editable_by_tenant !== false;
    return {
      canEditAdmissions: hasRbac && flagEnabled,
      missingRbac: !hasRbac,
      pausedByTenant: hasRbac && !flagEnabled,
    };
  }, [user, featureFlags]);
}
